'use client';

import { useEffect, FormEvent, useState } from 'react';
import { loadStripe, PaymentMethod, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Submit, Box } from '@instamenu/ui-system-next';
import { captureClientException } from '@instamenu/posthog/client';
import { useCreateSetupIntentMutation } from './mutations.generated';
import { palette } from '@instamenu/ui-vanilla-extract';
import { scrollableBox } from '../payment-method/payment-method.css';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '');

export interface PaymentMethodFormProps {
  returnUrl: string;
  onSubmit: (paymentMethod: PaymentMethod) => void;
}

export function PaymentMethodForm(props: PaymentMethodFormProps) {
  const [createIntentResult, createIntent] = useCreateSetupIntentMutation();

  useEffect(() => {
    createIntent({});
  }, [createIntent]);

  const elementsOptions: StripeElementsOptions = {
    loader: 'always',
    clientSecret: createIntentResult.data?.createSetupIntent?.clientSecret,
    fonts: [
      {
        src: `url(/GothamSSm-Medium.otf)`,
        family: 'Gotham',
      },
    ],
    appearance: {
      variables: {
        colorPrimary: palette.primary,
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'Gotham',
        spacingUnit: '7px',
        borderRadius: '8px',
      },
      rules: {
        '.Input': {
          border: `1px solid #eaecf0`,
          padding: '7px 14px',
          margin: '0',
          fontSize: '16px',
          lineHeight: '32px',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        },
        '.Input:focus': {
          border: `1px solid ${palette.primary}`,
          borderColor: '#315F64',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        },
        '.Label': {
          fontWeight: '500',
        },
      },
    },
  };

  if (!elementsOptions.clientSecret) return null;

  return (
    <Elements key={elementsOptions.clientSecret} stripe={stripePromise} options={elementsOptions}>
      <Form {...props} />
    </Elements>
  );
}

function Form({ returnUrl, onSubmit }: PaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    elements?.getElement('payment')?.on('ready', () => setIsReady(true));
  }, [elements]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: returnUrl, expand: ['payment_method'] },
      });

      const payment_method = result.setupIntent?.payment_method;
      if (!payment_method || typeof payment_method === 'string')
        throw 'Payment setup failed. Please check your payment details and try again.';
      else onSubmit(payment_method);
    } catch (e) {
      // Stripe will show validation errors in the PaymentElement automatically
      captureClientException(e, {
        source: 'ui-storefront.payment-method-form.confirm-setup',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="payment-method-form">
      <Box className={scrollableBox} css={{ height: 'calc(100dvh)' }}>
        <PaymentElement />
      </Box>
      <Submit disabled={!isReady} css={{ width: '100%' }} loading={loading}>
        Add Payment Method
      </Submit>
    </form>
  );
}
